import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Navbar from '../../components/Navbar';
import { PageHeader } from '../../components/ui/page-header';
import { DoorOpen, DoorClosed, Plus, Trash2, Clock, History, Loader2, Store } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtTime = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); }
  catch { return '—'; }
};

const VendorCounters = () => {
  const [counters, setCounters] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [newName, setNewName] = useState('');
  const [adding, setAdding] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/counters`, { withCredentials: true });
      setCounters(data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load counters');
    } finally { setLoading(false); }
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/counters/history`, { withCredentials: true });
      setHistory(data);
    } catch { /* noop */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (c) => {
    setBusy(c.id); setError('');
    const status = c.status === 'open' ? 'closed' : 'open';
    try {
      await axios.patch(`${API}/counters/${c.id}`, { status }, { withCredentials: true });
      await load();
      if (showHistory) loadHistory();
    } catch (e) { setError(e?.response?.data?.detail || 'Could not update counter'); }
    finally { setBusy(null); }
  };

  const addCounter = async () => {
    const name = newName.trim();
    if (!name) return;
    setAdding(true); setError('');
    try {
      await axios.post(`${API}/counters`, { name }, { withCredentials: true });
      setNewName('');
      await load();
    } catch (e) { setError(e?.response?.data?.detail || 'Could not add counter'); }
    finally { setAdding(false); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete counter "${c.name}"? Orders already placed are unaffected.`)) return;
    setBusy(c.id); setError('');
    try {
      await axios.delete(`${API}/counters/${c.id}`, { withCredentials: true });
      await load();
    } catch (e) { setError(e?.response?.data?.detail || 'Could not delete counter'); }
    finally { setBusy(null); }
  };

  const openCount = counters.filter((c) => c.status === 'open').length;

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8" data-testid="vendor-counters-page">
          <PageHeader
            title="Counters"
            subtitle={`Open a counter at the start of the day and close it at the end. Closed counters stop taking new orders. ${openCount}/${counters.length} open.`}
            icon={Store}
          />

          {error && <div data-testid="counters-error" className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

          {/* Add counter */}
          <div className="bg-card border border-border-light rounded-2xl p-4 mb-6 flex flex-wrap items-center gap-2">
            <input
              data-testid="counter-name-input"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addCounter()}
              placeholder="New counter name (e.g. South Indian)"
              className="flex-1 min-w-[180px] px-3 py-2 border border-border-light rounded-lg text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
            <button
              data-testid="add-counter-btn"
              onClick={addCounter}
              disabled={adding || !newName.trim()}
              className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-primary-hover disabled:opacity-50"
            >
              {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add counter
            </button>
          </div>

          {/* Counters list */}
          {loading ? (
            <div className="py-16 text-center text-text-muted"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
          ) : counters.length === 0 ? (
            <div className="bg-card border border-border-light rounded-2xl p-10 text-center text-text-muted" data-testid="counters-empty">
              No counters yet. Add one above — or tag your menu items with a counter name.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="counters-list">
              {counters.map((c) => {
                const open = c.status === 'open';
                return (
                  <div key={c.id} data-testid={`counter-card-${c.id}`} className="bg-card border border-border-light rounded-2xl p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-heading text-lg font-semibold text-text-primary truncate">{c.name}</p>
                        <span
                          data-testid={`counter-status-${c.id}`}
                          className={`inline-flex items-center gap-1 mt-1 text-xs font-medium px-2 py-0.5 rounded-full border ${open ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'}`}
                        >
                          {open ? <DoorOpen className="h-3 w-3" /> : <DoorClosed className="h-3 w-3" />}
                          {open ? 'Open' : 'Closed'}
                        </span>
                      </div>
                      <button
                        data-testid={`counter-delete-${c.id}`}
                        onClick={() => remove(c)}
                        className="text-text-muted hover:text-red-600 p-1"
                        title="Delete counter"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    <p className="text-xs text-text-muted mt-3 flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {open ? `Opened ${fmtTime(c.opened_at)}` : `Closed ${fmtTime(c.closed_at)}`}
                    </p>
                    <button
                      data-testid={`counter-toggle-${c.id}`}
                      onClick={() => toggle(c)}
                      disabled={busy === c.id}
                      className={`mt-4 w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors disabled:opacity-50 ${open ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-emerald-600 text-white hover:bg-emerald-700'}`}
                    >
                      {busy === c.id ? <Loader2 className="h-4 w-4 animate-spin" /> : (open ? <DoorClosed className="h-4 w-4" /> : <DoorOpen className="h-4 w-4" />)}
                      {open ? 'Close counter' : 'Open counter'}
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* History */}
          <div className="mt-8">
            <button
              data-testid="counters-history-toggle"
              onClick={() => { const n = !showHistory; setShowHistory(n); if (n) loadHistory(); }}
              className="flex items-center gap-2 text-sm font-medium text-text-secondary hover:text-primary"
            >
              <History className="h-4 w-4" /> {showHistory ? 'Hide' : 'Show'} open/close history
            </button>
            {showHistory && (
              <div className="mt-3 bg-card border border-border-light rounded-2xl divide-y divide-border-light" data-testid="counters-history">
                {history.length === 0 ? (
                  <p className="p-5 text-sm text-text-muted">No history yet.</p>
                ) : history.map((e) => (
                  <div key={e.id} className="px-5 py-3 flex items-center justify-between text-sm">
                    <span className="text-text-primary"><span className="font-medium capitalize">{e.action}</span> · {e.name}</span>
                    <span className="text-text-muted text-xs">{fmtTime(e.at)} · {e.by}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default VendorCounters;
